import { useState } from 'react';
import { Link } from 'react-router-dom';
import { errorDetail, errorMessage } from '../lib/api-error';
import { date, money, percent } from '../lib/format';
import { TAX_RESERVE_WARNINGS, useTaxReserve, type TaxReserve } from '../lib/tax-reserve-api';
import {
  Badge,
  Card,
  CardHeader,
  ErrorState,
  PageHeader,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

/**
 * "NE KADAR KENARA AYIRMALIYIM" — bu yazılımın en değerli tek rakamı.
 *
 * ── EKRANIN CEVAPLADIĞI SORU ──────────────────────────────────────────────
 * Kesilen faturanın kaç euro'su gerçekten mükellefin? Cevap tek bir oran
 * değildir: serbest meslek kârı MAAŞIN ÜSTÜNE biner, yani ilk euro bile maaşın
 * bıraktığı dilimden vergilenir. Bijberoep'te tipik yük %58–63'tür ve yaygın
 * "%30 ayır" tavsiyesi bu profil için TEHLİKELİ DERECEDE DÜŞÜKTÜR.
 *
 * ── ÜÇ ŞEY EKRANDA AÇIKÇA YAZILI OLMAK ZORUNDA ────────────────────────────
 *  1. Bu bir TAHMİNDİR, beyan değil. Hiçbir tutar vergi dairesine gitmez.
 *  2. Sosyal primler bu yıl GEÇİCİ tahsil edilir, kesinleşmesi ~2 yıl sonra
 *     (regularisatie). Yani bu rakam "bu yılın nakit çağrıları" DEĞİL, kesin
 *     yükün tahminidir — ve asıl tehlike o gecikmiş düzeltmedir.
 *  3. Hangi belgelerin sayılmadığı (onaylanmamış alış ve fişler) ve oranların
 *     kaynağının doğrulanmamış olduğu.
 *
 * ⚠ BURADA HİÇBİR ORAN YOKTUR. Dilimler, eşikler ve tavanlar sunucudadır;
 * panelde ikinci bir kopya, endeksli tutarların değiştiği ilk Ocak'ta sessizce
 * eski rakamı göstermeye başlardı.
 */
export function ReportsPage() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const reserve = useTaxReserve(year);

  return (
    <>
      <PageHeader
        title="Ne kadar kenara ayırmalıyım?"
        description="Sosyal prim + gelir vergisi TAHMİNİ. Beyan değildir; hiçbir tutar idareye gönderilmez."
      />

      <div className="mb-4 flex items-center gap-2 text-sm">
        <label htmlFor="income-year">Gelir yılı</label>
        <select
          id="income-year"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="rounded-[var(--r-md)] border border-[var(--c-border-strong)] bg-[var(--c-surface)] px-2 py-1"
        >
          {[currentYear, currentYear - 1].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      {reserve.isPending ? (
        <Card>
          <div className="px-4 py-10 text-center">
            <Spinner />
          </div>
        </Card>
      ) : reserve.isError ? (
        <Card>
          <ErrorState message={errorMessage(reserve.error)} detail={errorDetail(reserve.error)} />
        </Card>
      ) : (
        <ReserveView data={reserve.data} />
      )}
    </>
  );
}

function ReserveView({ data }: { data: TaxReserve }) {
  const a = data.amounts;

  return (
    <div className="space-y-4">
      <Card>
        <div className="px-4 py-5">
          <p className="text-sm text-[var(--c-ink-soft)]">
            {data.incomeYear} gelir yılı · {date(data.asOf)} tarihli defterlere göre
          </p>
          <p className="mt-1 text-4xl font-semibold tabular-nums">{money(a.totalReserve)}</p>
          <p className="mt-1 text-sm text-[var(--c-ink-soft)]">
            {data.reservePctOfProfit === null
              ? 'Bu yıl için kâr görünmüyor.'
              : `Kârın ${percent(data.reservePctOfProfit)}’i. Kâr: ${money(a.profit)} · kenara ayırdıktan sonra kalan: ${money(a.netAfterReserve)}`}
          </p>

          {/* ⚠ "Bu yıl ödenecek" DEĞİL: primler geçici tahsil edilir, kesin
              hesap ~2 yıl sonra gelir ve asıl sürpriz odur. */}
          <p className="mt-3 text-xs text-[var(--c-ink-faint)]">
            ⚠ Bu rakam <strong>kesin yükün tahminidir</strong>, bu yılın nakit çağrılarının toplamı
            değil. Sosyal primler {data.incomeYear} için geçici tahsil edilir; kesin hesap
            (regularisatie) yaklaşık iki yıl sonra gelir ve aradaki fark o zaman istenir. Gelir
            vergisi beyanı {data.assessmentYear} yılında verilir.
          </p>
        </div>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader
            title="Sosyal prim"
            description="Kârın üstünden hesaplanır; gelir vergisinde gider olarak düşülür."
          />
          <Table>
            <tbody>
              <Line label="Prim matrahı (hasılat − gider)" value={money(a.socialBase)} />
              <Line label="Prim" value={money(a.socialContribution)} />
              <Line
                label={`Kasa yönetim ücreti (${percent(data.managementFeePct)})`}
                value={money(a.managementFee)}
              />
              <Line label="Kasaya toplam" value={money(a.socialTotal)} strong />
            </tbody>
          </Table>
          <p className="px-4 pb-4 text-xs text-[var(--c-ink-faint)]">
            ⚠ Primin matrahından primin kendisi düşülmedi. Yasal matrah daha düşüktür (ödenen
            primler gider yazılır); hangi primin düşüleceği o yıl fiilen ödenen geçici primlere
            bağlı olduğundan, tahmin bilerek <strong>güvenli tarafta</strong> tutuldu.
          </p>
        </Card>

        <Card>
          <CardHeader
            title="Gelir vergisi (artış)"
            description="Maaşın üstüne binen kârın DOĞURDUĞU fark — maaşın kendi vergisi değil."
          />
          <Table>
            <tbody>
              <Line label="Maaş tahmini (vergiye esas)" value={money(a.salariedIncome)} />
              <Line label="Vergilenecek mesleki gelir" value={money(a.taxableProfessionalIncome)} />
              <Line label="Marjinal dilim" value={percent(data.marginalRatePct)} />
              <Line label="Vergi artışı" value={money(a.incomeTaxIncrement)} />
              <Line
                label={`Belediye vergisi (${percent(data.communalTaxPct)})`}
                value={money(a.communalTax)}
              />
            </tbody>
          </Table>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Rakam nereden geldi"
          description="Hasılat fatura tarihine göre; onaylanmamış alış ve fişler sayılmaz."
        />
        <Table>
          <thead>
            <tr>
              <Th>Kalem</Th>
              <Th>Tutar</Th>
              <Th>Belge</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td>Hasılat (KDV hariç, kredi notları düşülmüş)</Td>
              <Td>{money(a.revenue)}</Td>
              <Td>
                <Link className="text-[var(--c-brand)] hover:underline" to="/invoices">
                  {data.counts.salesInvoices} fatura
                </Link>
              </Td>
            </tr>
            <tr>
              <Td>Gerçek gider (gelir vergisinde indirilebilir kısım)</Td>
              <Td>{money(a.actualCosts)}</Td>
              <Td>
                <Link className="text-[var(--c-brand)] hover:underline" to="/purchases">
                  {data.counts.purchases} alış
                </Link>
                {' · '}
                <Link className="text-[var(--c-brand)] hover:underline" to="/expenses">
                  {data.counts.expenses} fiş
                </Link>
              </Td>
            </tr>
            {/* ⚠ Yatırım GİDER DEĞİLDİR ama görünmezse "aldım, nerede?"
                sorusunu doğurur. Ayrı satırda, gider toplamının DIŞINDA. */}
            <tr>
              <Td>
                Yatırım — duran varlık (kutu 83)
                <span className="block text-xs text-[var(--c-ink-faint)]">
                  gider değil: amortismanla indirilir
                </span>
              </Td>
              <Td>{money(data.investmentCost)}</Td>
              <Td>
                <Link className="text-[var(--c-brand)] hover:underline" to="/assets">
                  duran varlıklar
                </Link>
              </Td>
            </tr>
            <tr>
              <Td>
                Amortisman (gider toplamına DÂHİL)
                <span className="block text-xs text-[var(--c-ink-faint)]">
                  yatırımın bu yıla düşen, indirilebilir payı
                </span>
              </Td>
              <Td>{money(data.depreciation)}</Td>
              <Td>
                <span className="text-xs text-[var(--c-ink-faint)]">
                  {data.depreciatingAssets} varlık
                </span>
              </Td>
            </tr>
            <tr>
              <Td>
                Uygulanan gider yöntemi
                <span className="block text-xs text-[var(--c-ink-faint)]">
                  {data.costMethod === 'FORFAIT'
                    ? `Forfait — tavan ${money(a.forfaitBreakEven)} kârda dolar`
                    : 'Gerçek gider'}
                </span>
              </Td>
              <Td>{money(a.appliedCosts)}</Td>
              <Td>
                <span className="text-xs text-[var(--c-ink-faint)]">
                  diğer yöntem: {money(a.alternativeCosts)}
                </span>
              </Td>
            </tr>
          </tbody>
        </Table>
      </Card>

      {data.warnings.length > 0 && (
        <Card>
          <CardHeader
            title="Bu rakamı okurken"
            description="Her satır, tahminin hangi yönde saptığını söyler."
          />
          <ul className="space-y-2 px-4 py-4 text-sm">
            {data.warnings.map((code) => (
              <li key={code} className="flex gap-2">
                <Badge tone={code === 'INCOME_TAX_RATES_UNVERIFIED' ? 'warn' : 'neutral'}>!</Badge>
                <span>{TAX_RESERVE_WARNINGS[code] ?? code}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <tr>
      <Td>{strong ? <strong>{label}</strong> : label}</Td>
      <Td>
        <span className={strong ? 'font-semibold tabular-nums' : 'tabular-nums'}>{value}</span>
      </Td>
    </tr>
  );
}
